// B-131 — where an office is, for the dashboard's map. Only from what the registry declares, never
// guessed from a name:
//
//   1. its DANE municipality code (`dane`: Banco Agrario's, Bancamía's and Davivienda's, B-136) — exact;
//   2. its declared `city`, matched to ONE municipality of DIVIPOLA, using the office's department
//      or region to tell apart two municipalities with the same name.
//
// Never its name: Bancamía's offices are named after Bogotá neighbourhoods ("RESTREPO", "KENNEDY"),
// and matching those names to municipalities would put a Bogotá office in Restrepo, Meta — so the
// registry carries Bogotá's code for them. An office that cannot be placed is listed beside the map.
//
// The municipalities are DANE's DIVIPOLA (src/data/municipiosColombia.json): code -> [lat, lng,
// name, department], the municipality's reference point.
import datos from "../data/municipiosColombia.json";

const MUNICIPIOS = datos.municipios;

/** Uppercase, no accents, single spaces — how two spellings of one place compare. */
export const normalizarLugar = (texto) =>
  String(texto || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

let indicePorNombre = null;

/** Municipality name -> the codes carrying it (several towns share a name). Built once. */
function porNombre() {
  if (indicePorNombre) return indicePorNombre;
  indicePorNombre = new Map();
  Object.entries(MUNICIPIOS).forEach(([codigo, [, , nombre]]) => {
    const clave = normalizarLugar(nombre);
    indicePorNombre.set(clave, [...(indicePorNombre.get(clave) || []), codigo]);
  });
  return indicePorNombre;
}

const lugar = (codigo, por) => {
  const [lat, lng, municipio, departamento] = MUNICIPIOS[codigo];
  return { lat, lng, municipio, departamento, dane: codigo, por };
};

/**
 * The municipalities a city name can mean: the exact name, else the ones whose official name
 * starts with it ("CARTAGENA" is "CARTAGENA DE INDIAS").
 */
function candidatos(ciudad) {
  const clave = normalizarLugar(ciudad);
  if (!clave) return [];
  const exactos = porNombre().get(clave);
  if (exactos) return exactos;
  const resultado = [];
  porNombre().forEach((codigos, nombre) => {
    if (nombre.startsWith(`${clave} `)) resultado.push(...codigos);
  });
  return resultado;
}

/**
 * Where an office is, or null when the registry does not say.
 *
 * @param {{ dane?: string|null, city?: string|null, departamento?: string|null, region?: string|null }} oficina
 * @returns {{ lat: number, lng: number, municipio: string, departamento: string, dane: string, por: "dane"|"ciudad" } | null}
 */
export function ubicarOficina(oficina) {
  if (!oficina) return null;
  if (oficina.dane && MUNICIPIOS[oficina.dane]) return lugar(oficina.dane, "dane");

  const posibles = candidatos(oficina.city);
  if (posibles.length === 1) return lugar(posibles[0], "ciudad");
  if (posibles.length > 1) {
    // Two towns with one name: the office's department (or region, which Davivienda writes as
    // the department) must pick exactly one, or the office is not placed.
    const pista = normalizarLugar(oficina.departamento || oficina.region);
    const delDepartamento = pista
      ? posibles.filter((codigo) => normalizarLugar(MUNICIPIOS[codigo][3]).startsWith(pista))
      : [];
    if (delDepartamento.length === 1) return lugar(delDepartamento[0], "ciudad");
  }
  return null;
}
